import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Fieldset,
  FileButton,
  Flex,
  Grid,
  Group,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconTrash } from '@tabler/icons-react';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useProfile } from 'init/use-enforce-profile';
import { useAtom } from 'jotai';
import { i18nKeys, tKeys, useTranslation } from 'locales';
import { useEffect, useRef, useState } from 'react';
import { isNonNull } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import type { Profile as TProfile } from 'shared/hooks/use-load-profile';
import {
  type UpdateProfileForm,
  UpdateProfileFormSchema,
  useUpdateProfile,
} from 'shared/hooks/use-update-profile';
import { APPLICATION_LANGUAGES } from 'types';
import { KEYBOARD_LAYOUTS, KeyboardLayoutAtom } from './keyboard-layout';

import SignatureEditorModal from './SignatureEditorModal';

function profileToProfileForm(profile: TProfile): UpdateProfileForm {
  return {
    firstName: profile.name.first,
    lastName: profile.name.last,
    email: profile.email,
    phone: profile.phone,
    locale: profile.locale,
    pageLimit: profile.preferences.itemsPerPage,
    receiveDailyEmails: profile.preferences.emails.daily,
    receiveWeeklyEmails: profile.preferences.emails.weekly,
    receiveNotificationEmails: profile.preferences.emails.notifications,
    receiveTaskEmails: profile.preferences.emails.tasks,
  };
}

export const Profile = () => {
  const { t } = useTranslation();
  const profile = useProfile();
  const { updateProfile, isUpdateProfileLoading } = useUpdateProfile();
  const [keyboardLayout, setKeyboardLayout] = useAtom(KeyboardLayoutAtom);
  const [isSignatureModalOpen, { open: openModal, close: closeModal }] = useDisclosure();

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [shouldShowFakeSaveButton, setShouldShowFakeSaveButton] = useState(false);
  const resetRef = useRef<() => void>(null);

  const canResetImage =
    !isUpdateProfileLoading && (isNonNull(avatarFile) || isNonNull(profile?.profilePicture));

  const form = useForm<UpdateProfileForm>({
    validate: schemaResolver(UpdateProfileFormSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setInitialValues(profileToProfileForm(profile));
    form.setValues(profileToProfileForm(profile));
  }, [JSON.stringify(profile)]);

  const personalInformationFields: Array<keyof UpdateProfileForm> = [
    'firstName',
    'lastName',
    'email',
    'phone',
  ];

  const personalPreferencesFields: Array<keyof UpdateProfileForm> = [
    'locale',
    'pageLimit',
    'receiveDailyEmails',
    'receiveWeeklyEmails',
    'receiveNotificationEmails',
    'receiveTaskEmails',
  ];

  const paginationOptions = [
    { label: `10 ${t(i18nKeys.PER_PAGE)}`, value: '10' },
    { label: `20 ${t(i18nKeys.PER_PAGE)}`, value: '20' },
    { label: `50 ${t(i18nKeys.PER_PAGE)}`, value: '50' },
    { label: `100 ${t(i18nKeys.PER_PAGE)}`, value: '100' },
  ];

  const canSavePersonalInformation =
    personalInformationFields.some(field => form.isDirty(field)) &&
    personalInformationFields.every(field => form.isValid(field));

  const canSavePersonalPreferences = personalPreferencesFields.some(field => form.isDirty(field));

  const saveDirtyFields = (fields: Array<keyof UpdateProfileForm>) =>
    updateProfile(
      Object.fromEntries(
        fields.filter(field => form.isDirty(field)).map(field => [field, form.values[field]]),
      ),
    );

  const clearFile = () => {
    setAvatarFile(null);
    resetRef.current?.();
    updateProfile({ avatar: null });
  };

  return (
    <>
      <PageTitle>{t(i18nKeys.SETTINGS.PROFILE.EDIT)}</PageTitle>
      <Grid pb="lg" pr="lg">
        <Grid.Col span={5}>
          <Panel
            title={t(i18nKeys.PERSONAL_INFO)}
            withBorder
            fullHeight
            pinFooter
            footer={
              <SaveButton
                onClick={() => saveDirtyFields(personalInformationFields)}
                disabled={!canSavePersonalInformation}
                loading={isUpdateProfileLoading}
                hideIcon
              />
            }
          >
            <Stack my="md">
              <TextInput
                label={t(i18nKeys.FORM.FIRSTNAME)}
                required
                {...form.getInputProps('firstName')}
              />
              <TextInput
                label={t(i18nKeys.FORM.LASTNAME)}
                required
                {...form.getInputProps('lastName')}
              />
              <TextInput label={t(i18nKeys.EMAIL)} required {...form.getInputProps('email')} />
              <TextInput
                label={t(i18nKeys.PHONE)}
                {...form.getInputProps('phone')}
                onChange={event => {
                  const filtered = event.currentTarget.value.replace(/[^0-9+]/g, '');
                  form.setFieldValue(`phone`, filtered);
                }}
              />
            </Stack>
          </Panel>
        </Grid.Col>
        <Grid.Col span={4}>
          <Panel
            title={t(i18nKeys.SIGNATURE)}
            withBorder
            fullHeight
            pinFooter
            footer={
              <>
                <ActionIcon
                  disabled={!profile?.signature}
                  variant="light"
                  size={36}
                  color="red"
                  aria-label="Settings"
                  onClick={() => updateProfile({ signature: null })}
                >
                  <IconTrash size={18} stroke={1.5} />
                </ActionIcon>
                <SaveButton label={t(i18nKeys.EDIT)} onClick={openModal} hideIcon />
              </>
            }
          >
            <Box my="md">
              {profile?.signature ? (
                <HTML html={profile.signature} />
              ) : (
                <div>{t(i18nKeys.NO_SIGNATURE)}</div>
              )}
            </Box>
          </Panel>
        </Grid.Col>
        <Grid.Col span={3}>
          <Flex h="100%" align="center" justify="flex-start" direction="column" gap="xl">
            <Avatar
              variant="light"
              size="180px"
              radius="100%"
              color="blue"
              src={avatarFile ? URL.createObjectURL(avatarFile) : profile?.profilePicture}
            >
              {profile?.name.initials}
            </Avatar>
            <Group justify="center">
              <FileButton
                onChange={avatar => {
                  setAvatarFile(avatar);
                  updateProfile({ avatar });
                }}
                accept="image/png,image/jpeg,image/webp,image/jpg"
              >
                {props => (
                  <Button variant="light" loading={isUpdateProfileLoading} {...props}>
                    {t(i18nKeys.FORM.USER_SETTINGS.UPLOAD_BUTTON)}
                  </Button>
                )}
              </FileButton>
              <ActionIcon
                disabled={!canResetImage}
                variant="light"
                size={36}
                color="red"
                aria-label="Settings"
                onClick={clearFile}
              >
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            </Group>
          </Flex>
        </Grid.Col>
        <Grid.Col span={8}>
          <Panel
            title={t(i18nKeys.PERSONAL_PREFENCES)}
            withBorder
            fullHeight
            pinFooter
            footer={
              <SaveButton
                onClick={() => saveDirtyFields(personalPreferencesFields)}
                disabled={!canSavePersonalPreferences}
                loading={isUpdateProfileLoading}
                hideIcon
              />
            }
          >
            <Group justify="space-between" align="flex-start" my="md">
              <Stack w="50%">
                <Select
                  label={t(i18nKeys.FORM.USER_SETTINGS.INTERFACE_LANG)}
                  data={APPLICATION_LANGUAGES.map(item => ({
                    label: item.description,
                    value: item.value,
                  }))}
                  allowDeselect={false}
                  checkIconPosition="right"
                  {...form.getInputProps('locale')}
                />
                <Select
                  label={t(i18nKeys.FORM.USER_SETTINGS.PAGINATION)}
                  allowDeselect={false}
                  checkIconPosition="right"
                  data={paginationOptions}
                  {...form.getInputProps('pageLimit')}
                  value={String(form.getInputProps('pageLimit').value)}
                />
              </Stack>
              <Fieldset legend={t(i18nKeys.SETTINGS.STAFF.RECEIVE_EMAIL)} w="40%">
                <Stack>
                  <Switch
                    color="purple.4"
                    size="xs"
                    label={t(i18nKeys.SETTINGS.STAFF.DAILY)}
                    {...form.getInputProps('receiveDailyEmails', { type: 'checkbox' })}
                  />
                  <Switch
                    color="purple.4"
                    size="xs"
                    label={t(i18nKeys.SETTINGS.STAFF.WEEKLY)}
                    {...form.getInputProps('receiveWeeklyEmails', { type: 'checkbox' })}
                  />
                  <Switch
                    color="purple.4"
                    size="xs"
                    label={t(i18nKeys.SETTINGS.STAFF.NOTIFICATION)}
                    {...form.getInputProps('receiveNotificationEmails', { type: 'checkbox' })}
                  />
                  <Switch
                    color="purple.4"
                    size="xs"
                    label={t(i18nKeys.SETTINGS.STAFF.TASK)}
                    {...form.getInputProps('receiveTaskEmails', { type: 'checkbox' })}
                  />
                </Stack>
              </Fieldset>
            </Group>
          </Panel>
        </Grid.Col>
        <Grid.Col span={4}>
          <Panel
            title={t(tKeys.SETTINGS.PROFILE.HOTKEYS.TITLE)}
            withBorder
            fullHeight
            pinFooter
            footer={
              <SaveButton
                onClick={() => setShouldShowFakeSaveButton(false)}
                disabled={!shouldShowFakeSaveButton}
                hideIcon
              />
            }
          >
            <Stack my="md">
              <Select
                label={t(tKeys.SETTINGS.PROFILE.HOTKEYS.LABEL)}
                description={t(tKeys.SETTINGS.PROFILE.HOTKEYS.DESCRIPTION)}
                value={keyboardLayout}
                allowDeselect={false}
                onChange={val => {
                  setKeyboardLayout(val as typeof keyboardLayout);
                  setShouldShowFakeSaveButton(true);
                }}
                data={KEYBOARD_LAYOUTS}
              />
            </Stack>
          </Panel>
        </Grid.Col>
      </Grid>
      <SignatureEditorModal
        signature={profile?.signature ?? null}
        isOpen={isSignatureModalOpen}
        onClose={closeModal}
        onSave={signature => updateProfile({ signature })}
      />
    </>
  );
};
